import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Globe, Search } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ORIENT_COLORS = { care: "#10b981", utility: "#3b82f6", creative: "#a855f7", extraction: "#ef4444" };

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-[#0c1222] border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="font-bold text-white">{d?.country || d?.name}</p>
      <p className="text-cyan-400">Policies: {d?.count}</p>
      <p className="text-emerald-400">Avg CII: {d?.avg_cii}</p>
    </div>
  );
};

export const PolicyRegional = () => {
  const [policies, setPolicies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState("");

  useEffect(() => {
    Promise.allSettled([
      axios.get(`${API}/policy/stats`),
      axios.get(`${API}/policy/list?limit=500`),
    ]).then(([s, p]) => {
      if (s.status === "fulfilled") setStats(s.value.data);
      if (p.status === "fulfilled") setPolicies(p.value.data.policies || []);
    }).finally(() => setLoading(false));
  }, []);

  const countryData = useMemo(() => {
    const map = {};
    const filtered = selectedRegion ? policies.filter(p => p.region === selectedRegion) : policies;
    filtered.forEach(p => {
      if (!map[p.country]) map[p.country] = { country: p.country, region: p.region, count: 0, total_cii: 0, orientations: {} };
      map[p.country].count++;
      map[p.country].total_cii += p.policy_cii || 0;
      map[p.country].orientations[p.value_orientation] = (map[p.country].orientations[p.value_orientation] || 0) + 1;
    });
    return Object.values(map).map(c => ({ ...c, avg_cii: Math.round(c.total_cii / c.count * 10) / 10 })).sort((a, b) => b.count - a.count);
  }, [policies, selectedRegion]);

  const chartData = useMemo(() => countryData.slice(0, 20).map(c => ({ name: c.country.length > 12 ? c.country.substring(0, 10) + ".." : c.country, ...c })), [countryData]);

  if (loading) return <div className="flex items-center justify-center py-32"><div className="w-10 h-10 rounded-full border-2 border-cyan-500/30 border-t-cyan-500 animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="policy-regional">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-black text-white">Regional Policy Landscape</h2>
          <p className="text-xs text-slate-400">{countryData.length} countries/organizations with AI policies</p>
        </div>
        <select value={selectedRegion} onChange={e => setSelectedRegion(e.target.value)} className="px-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white" data-testid="region-filter">
          <option value="">All Regions</option>
          {(stats?.regions || []).map(r => <option key={r.region} value={r.region}>{r.region} ({r.count})</option>)}
        </select>
      </div>

      {/* Region Cards */}
      {!selectedRegion && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(stats?.regions || []).map(r => {
            const ciiColor = r.avg_cii >= 60 ? "#10b981" : r.avg_cii >= 30 ? "#f59e0b" : "#ef4444";
            return (
              <button key={r.region} onClick={() => setSelectedRegion(r.region)} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 text-left hover:bg-white/[0.04] transition-colors" data-testid={`region-card-${r.region}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-bold text-white">{r.region}</h3>
                  <span className="text-lg font-black" style={{ color: ciiColor }}>{Number(r.avg_cii).toFixed(1)}</span>
                </div>
                <div className="h-1.5 bg-white/[0.04] rounded-full overflow-hidden mb-2">
                  <div className="h-full rounded-full" style={{ width: `${r.avg_cii}%`, background: ciiColor }} />
                </div>
                <p className="text-[10px] text-slate-400">{r.count} policies</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Chart */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">
          {selectedRegion ? `${selectedRegion}: Policies by Country` : "Top 20 Countries by Policy Count"}
        </h3>
        <div className="h-96">
          <ResponsiveContainer>
            <BarChart data={chartData} layout="vertical" margin={{ left: 5, right: 15, top: 5, bottom: 5 }}>
              <XAxis type="number" tick={{ fill: "#64748b", fontSize: 9 }} />
              <YAxis type="category" dataKey="name" width={100} tick={{ fill: "#94a3b8", fontSize: 9 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Policies" radius={[0, 4, 4, 0]}>
                {chartData.map((c) => <Cell key={c.name || c.region} fill={c.avg_cii >= 60 ? "#10b981" : c.avg_cii >= 30 ? "#f59e0b" : "#ef4444"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Country Table */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-white/[0.03] border-b border-white/[0.06]">
            <tr>
              <th className="text-left p-3 text-slate-400 font-bold">Country</th>
              <th className="text-left p-3 text-slate-400 font-bold">Region</th>
              <th className="text-center p-3 text-slate-400 font-bold">Policies</th>
              <th className="text-center p-3 text-slate-400 font-bold">Avg CII</th>
              <th className="text-left p-3 text-slate-400 font-bold">Orientation Mix</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.03]">
            {countryData.map(c => {
              const ciiColor = c.avg_cii >= 60 ? "#10b981" : c.avg_cii >= 30 ? "#f59e0b" : "#ef4444";
              return (
                <tr key={c.country} className="hover:bg-white/[0.02] transition-colors">
                  <td className="p-3 font-bold text-white">{c.country}</td>
                  <td className="p-3 text-slate-400">{c.region}</td>
                  <td className="p-3 text-center font-bold text-white">{c.count}</td>
                  <td className="p-3 text-center font-black" style={{ color: ciiColor }}>{c.avg_cii}</td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      {Object.entries(c.orientations).map(([o, cnt]) => (
                        <span key={o} className="text-[8px] px-1.5 py-0.5 rounded-full capitalize" style={{ color: ORIENT_COLORS[o], background: `${ORIENT_COLORS[o]}15`, border: `1px solid ${ORIENT_COLORS[o]}20` }}>{o}: {cnt}</span>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
